/**
 * English - `onboarding`: the "tell us about your business" form a new
 * merchant fills in before their first store exists. Its language pickers are
 * `i18n_entry.onboarding.*`.
 *
 * Keys: `fields.<field>.label|placeholder|hint` by the form field's name;
 * `validation.<field>_<problem>` for its errors; `business_types.<CODE>` by
 * the API's business type. Conventions: see `common.ts`.
 */
const onboarding: Record<string, string> = {
  title: 'Tell us about your business',
  intro:
    'This becomes your storefront address and appears on your GST invoices. You can change most of it later.',
  submit: 'Create business',
  submitting: 'Creating your business…',

  // The form's three groups, and the reassurances beside it on wide screens.
  'sections.business': 'Your business',
  'sections.contact': 'Contact & tax details',
  'sections.languages': 'Store languages',
  // The last two groups start folded, each showing what it will use.
  'sections.optional': 'Optional',
  'sections.contact_later': 'Add phone, address and GSTIN later',
  'sections.languages_more': '+{count} more',
  'perks.address': 'A storefront address reserved in your name',
  'perks.gst': 'GST-ready invoices from your first order',
  'perks.later': 'Change any of this later in Settings',

  'fields.name.label': 'Business name',
  'fields.name.placeholder': 'Kurta & Co',
  'fields.name.hint': 'We will reserve a matching storefront address for you.',
  'fields.owner_name.label': 'Your name',
  'fields.owner_name.hint': 'Optional. Shown to your team and on your invoices.',
  'validation.owner_name_short': 'Enter at least 2 characters, or leave it blank.',
  'fields.business_type.label': 'What do you sell?',
  'fields.email.label': 'Business email',
  'fields.phone.label': 'Phone (optional)',
  'fields.city.label': 'City (optional)',
  'fields.state.label': 'State (optional)',
  'fields.gstin.label': 'GSTIN (optional)',
  'fields.gstin.hint': 'Needed for GST invoices. Add it later if you are not registered yet.',

  'validation.name_required': 'Enter your business name.',
  'validation.business_type_required': 'Pick the closest category.',
  'validation.phone_invalid': 'Enter a valid Indian mobile number.',
  'validation.gstin_invalid': 'GSTIN looks like 22AAAAA0000A1Z5.',

  'business_types.FASHION': 'Fashion & apparel',
  'business_types.ELECTRONICS': 'Electronics',
  'business_types.GROCERY': 'Grocery & essentials',
  'business_types.BEAUTY': 'Beauty & personal care',
  'business_types.HOME_LIVING': 'Home & living',
  'business_types.HANDICRAFTS': 'Handicrafts',
  'business_types.FOOD_BEVERAGE': 'Food & beverage',
  'business_types.HEALTH_WELLNESS': 'Health & wellness',
  'business_types.JEWELLERY': 'Jewellery',
  'business_types.SERVICES': 'Services',
  'business_types.OTHER': 'Something else',

  // The industry picker: `sectors.<sector>` are its shelves, `industries.<key>`
  // its rows, by the keys in `features/tenants/api/industries.ts`.
  'fields.industry.label': 'What kind of business is it?',
  'fields.industry.placeholder': 'Select your industry',
  'validation.industry_required': 'Select your industry.',
  'fields.seed_sample_catalog.label': 'Start with sample categories & products',
  'fields.seed_sample_catalog.hint':
    'We add 5 categories and 5 sample products for your industry, live in your storefront preview. Remove them anytime.',

  'sectors.retail': 'Retail',
  'sectors.food': 'Food',
  'sectors.health_wellness': 'Health & Wellness',
  'sectors.services': 'Services',

  'industries.fashion_apparel': 'Fashion & Apparel',
  'industries.beauty_cosmetics': 'Beauty & Cosmetics',
  'industries.jewellery': 'Jewellery',
  'industries.electronics': 'Electronics',
  'industries.home_kitchen': 'Home & Kitchen',
  'industries.furniture': 'Furniture',
  'industries.sports': 'Sports',
  'industries.books_stationery': 'Books & Stationery',
  'industries.toys_baby': 'Toys & Baby',
  'industries.automotive': 'Automotive',
  'industries.pet_supplies': 'Pet Supplies',
  'industries.grocery': 'Grocery',
  'industries.bakery': 'Bakery',
  'industries.restaurant_food_delivery': 'Restaurant & Food Delivery',
  'industries.flowers_gifts': 'Flowers & Gifts',
  'industries.health_wellness': 'Health & Wellness',
  'industries.pharmacy_medical': 'Pharmacy & Medical',
  'industries.salon_beauty_services': 'Salon & Beauty Services',
  'industries.clinics': 'Clinics',
  'industries.consultancy': 'Consultancy',
  'industries.repair_maintenance': 'Repair & Maintenance',
  'industries.other_services': 'Other Services',
  // Not a pickable industry: the chip for themes that suit any shop.
  'industries.general': 'General',
};

export default onboarding;
